import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subDays, subHours, format, eachDayOfInterval, eachHourOfInterval, parseISO, startOfHour } from "date-fns";

interface PackagePickupData {
  date: string;
  fullDate: string;
  containers: number;
  registrations: number;
  avgContainers: number;
  avgPickupTime: number | null;
}

type TimeFilterType = 'timestamp' | 'date' | 'all';

interface TimeFilter {
  type: TimeFilterType;
  value: Date | null;
}

export const useRealtimePackagePickups = (timeRange: string, refreshInterval: number) => {
  const getTimeFilter = (): TimeFilter => {
    const now = new Date();
    switch (timeRange) {
      case "1h":
        return { type: 'timestamp', value: subHours(now, 1) };
      case "4h":
        return { type: 'timestamp', value: subHours(now, 4) };
      case "24h":
        return { type: 'timestamp', value: subDays(now, 1) };
      case "7d":
        return { type: 'date', value: subDays(now, 7) };
      case "30d":
        return { type: 'date', value: subDays(now, 30) };
      case "90d":
        return { type: 'date', value: subDays(now, 90) };
      case "all":
        return { type: 'all', value: null };
      default:
        return { type: 'date', value: subDays(now, 7) };
    }
  };

  const getDateFormat = () => {
    switch (timeRange) {
      case "1h":
      case "4h":
        return "HH:mm";
      case "24h":
        return "dd/MM HH:mm";
      case "7d":
      case "30d":
        return "dd/MM";
      case "90d":
        return "dd MMM";
      case "all":
        return "dd MMM yy";
      default:
        return "dd/MM";
    }
  };

  const { data: pickupData, refetch, isLoading } = useQuery({
    queryKey: ["package-pickups", timeRange],
    queryFn: async (): Promise<PackagePickupData[]> => {
      const timeFilter = getTimeFilter();
      
      let query = supabase
        .from("package_pickup_logs")
        .select("pickup_date, aantal_rolcontainers, pickup_time, created_at")
        .range(0, 100000);

      if (timeFilter.type === 'timestamp') {
        // Voor 1h, 4h, 24h: filter op created_at timestamp
        query = query.gte("created_at", timeFilter.value!.toISOString());
      } else if (timeFilter.type === 'date') {
        // Voor 7d, 30d, 90d: filter op pickup_date
        query = query.gte("pickup_date", format(timeFilter.value!, "yyyy-MM-dd"));
      }
      // Voor 'all': geen filter, haal alles op

      const { data, error } = await query.order("pickup_date", { ascending: true });

      if (error) throw error;
      
      console.log(`📊 Fetched ${data.length} records from database`);

      // Helper function to convert time string to hours since midnight
      const timeToHours = (timeString: string): number => {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours + minutes / 60;
      };

      // Timezone-safe weekend check - parse direct uit YYYY-MM-DD string
      const isWeekend = (dateString: string): boolean => {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const dayOfWeek = date.getUTCDay();
        return dayOfWeek === 0 || dayOfWeek === 6;
      };

      const dateFormat = getDateFormat();
      
      // Voor korte ranges (1h, 4h, 24h): groepeer per uur op basis van created_at
      if (timeFilter.type === 'timestamp') {
        const hourlyStats = data.reduce((acc: Record<string, {
          containers: number;
          registrations: number;
          pickups: number[];
          pickupTimes: number[];
        }>, item) => {
          const hourKey = startOfHour(new Date(item.created_at)).toISOString();
          
          if (!acc[hourKey]) {
            acc[hourKey] = {
              containers: 0,
              registrations: 0,
              pickups: [],
              pickupTimes: []
            };
          }
          
          acc[hourKey].containers += item.aantal_rolcontainers || 0;
          acc[hourKey].registrations++;
          acc[hourKey].pickups.push(item.aantal_rolcontainers || 0);
          
          if (item.pickup_time) {
            acc[hourKey].pickupTimes.push(timeToHours(item.pickup_time));
          }
          
          return acc;
        }, {});
        
        // Gap filling voor uren - ALLEEN werkdagen
        const now = new Date();
        const startTime = timeFilter.value!;
        const allHours = eachHourOfInterval({ start: startTime, end: now });
        
        allHours.forEach(hour => {
          const dateStr = format(hour, 'yyyy-MM-dd');
          // Skip weekenden
          if (isWeekend(dateStr)) return;
          
          const key = hour.toISOString();
          if (!hourlyStats[key]) {
            hourlyStats[key] = {
              containers: 0,
              registrations: 0,
              pickups: [],
              pickupTimes: []
            };
          }
        });
        
        const result = Object.entries(hourlyStats)
          .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
          .filter(([hourISO]) => {
            const dateStr = format(new Date(hourISO), 'yyyy-MM-dd');
            return !isWeekend(dateStr);
          })
          .map(([hourISO, stats]) => ({
            date: format(new Date(hourISO), dateFormat),
            fullDate: hourISO,
            containers: stats.containers,
            registrations: stats.registrations,
            avgContainers: stats.pickups.length > 0 
              ? Math.round(stats.pickups.reduce((a, b) => a + b, 0) / stats.pickups.length * 10) / 10
              : 0,
            avgPickupTime: stats.pickupTimes.length > 0
              ? Math.round(stats.pickupTimes.reduce((a, b) => a + b, 0) / stats.pickupTimes.length * 10) / 10
              : null
          }));
        
        console.log(`📈 Hourly data: ${result.length} hours from ${result[0]?.fullDate} to ${result[result.length - 1]?.fullDate}`);
        return result;
      }
      
      // Voor langere ranges (7d, 30d, 90d, all): groepeer per dag op basis van pickup_date
      const dailyStats = data.reduce((acc: Record<string, {
        containers: number;
        registrations: number;
        pickups: number[];
        pickupTimes: number[];
      }>, item) => {
        const date = item.pickup_date;
        
        if (!acc[date]) {
          acc[date] = {
            containers: 0,
            registrations: 0,
            pickups: [],
            pickupTimes: []
          };
        }
        
        acc[date].containers += item.aantal_rolcontainers || 0;
        acc[date].registrations++;
        acc[date].pickups.push(item.aantal_rolcontainers || 0);
        
        if (item.pickup_time) {
          acc[date].pickupTimes.push(timeToHours(item.pickup_time));
        }
        
        return acc;
      }, {});

      // Gap filling voor dagen - ALLEEN werkdagen
      const dateKeys = Object.keys(dailyStats).sort();
      if (dateKeys.length > 0) {
        const minDate = parseISO(dateKeys[0]);
        const maxDate = parseISO(dateKeys[dateKeys.length - 1]);
        const allDays = eachDayOfInterval({ start: minDate, end: maxDate });
        
        allDays.forEach(day => {
          const key = format(day, "yyyy-MM-dd");
          // Skip weekenden
          if (isWeekend(key)) return;
          if (!dailyStats[key]) {
            dailyStats[key] = {
              containers: 0,
              registrations: 0,
              pickups: [],
              pickupTimes: []
            };
          }
        });
      }
      
      const result = Object.entries(dailyStats)
        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
        .filter(([date]) => !isWeekend(date)) // Filter weekenden - date is already "YYYY-MM-DD"
        .map(([date, stats]) => ({
          date: format(new Date(date), dateFormat),
          fullDate: date,
          containers: stats.containers,
          registrations: stats.registrations,
          avgContainers: stats.pickups.length > 0 
            ? Math.round(stats.pickups.reduce((a, b) => a + b, 0) / stats.pickups.length * 10) / 10
            : 0,
          avgPickupTime: stats.pickupTimes.length > 0
            ? Math.round(stats.pickupTimes.reduce((a, b) => a + b, 0) / stats.pickupTimes.length * 10) / 10
            : null
        }));
      
      console.log(`📈 Daily data: ${result.length} days from ${result[0]?.fullDate} to ${result[result.length - 1]?.fullDate}`);
      return result;
    },
    refetchInterval: refreshInterval * 1000,
  });

  // Real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('package-pickups-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'package_pickup_logs'
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  return {
    pickupData: pickupData || [],
    isLoading
  };
};